import {Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import * as React from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {useTranslation} from "react-i18next";
import {InsightsTooltip, TooltipDelta, TooltipRow, TooltipTitle} from "./InsightsTooltip";

export type Trend = "up" | "down" | "neutral";

export type ComparisonTone = "positive" | "negative" | "neutral";

export interface StatComparison {
    change?: string;
    trend: Trend;
    lowerIsBetter?: boolean;
    previous?: string;
    /**
     * An explicit verdict on the movement, for figures whose direction is judged upstream. Without it
     * the chip reads the direction off `trend` and `lowerIsBetter`.
     */
    tone?: ComparisonTone;
}

/** Whether a movement is the good direction: an upstream verdict wins over the local rule. */
export const comparisonIsGood = (comparison: StatComparison): boolean =>
    comparison.tone != null
        ? comparison.tone === "positive"
        : comparison.trend === (comparison.lowerIsBetter ? "down" : "up");

export function trendOf(
    delta: number | null | undefined,
    format: (value: number) => string,
    lowerIsBetter = false
): StatComparison | undefined {
    if (delta == null) return undefined;

    const change = format(Math.abs(delta));
    const trend: Trend = change === format(0) ? "neutral" : delta > 0 ? "up" : "down";

    return {change, trend, lowerIsBetter};
}

export function comparisonOf(
    value: number | null | undefined,
    previous: number | null | undefined,
    format: (value: number) => string,
    lowerIsBetter = false,
    formatChange: (magnitude: number) => string = format
): StatComparison | undefined {
    if (value == null || previous == null) return undefined;

    const delta = Math.round(value) - Math.round(previous);
    const trend: Trend = delta > 0 ? "up" : delta < 0 ? "down" : "neutral";

    return {
        trend,
        change: trend === "neutral" ? undefined : formatChange(Math.abs(delta)),
        lowerIsBetter,
        previous: format(previous)
    };
}

interface HeadlineValueProps {
    value: string;
    suffix?: string;
}

export function HeadlineValue({value, suffix}: HeadlineValueProps) {
    return (
        <Stack direction={"row"} alignItems={"baseline"} gap={0.6} sx={{minWidth: 0}}>
            <Typography variant={"h5"} component={"p"} fontSize={24} fontWeight={700} lineHeight={1.15} noWrap>
                {value}
            </Typography>

            {suffix != null && (
                <Typography
                    component={"span"}
                    sx={{color: "text.secondary", fontSize: 14, fontWeight: 600, fontVariantNumeric: "tabular-nums"}}
                    noWrap
                >
                    {suffix}
                </Typography>
            )}
        </Stack>
    );
}

interface TrendChipProps {
    comparison?: StatComparison;
    panel?: React.ReactNode;
}

export function TrendChip({comparison, panel}: TrendChipProps) {
    if (comparison == null) return null;

    const moved = comparison.trend !== "neutral" && comparison.change != null;
    const good = comparisonIsGood(comparison);
    const TrendIcon = comparison.trend === "down" ? ArrowDownwardIcon : ArrowUpwardIcon;

    const chip = (
        <Stack
            component={"span"}
            direction={"row"}
            alignItems={"center"}
            gap={0.25}
            tabIndex={panel == null ? undefined : 0}
            sx={theme => {
                const color = moved
                    ? theme.palette[good ? "success" : "error"].main
                    : theme.palette.text.secondary;

                return {
                    flexShrink: 0,
                    minHeight: 28,
                    ...(moved ? {pl: 0.75, pr: 1.25} : {px: 1}),
                    border: "1px solid",
                    borderColor: alpha(color, 0.32),
                    borderRadius: 1,
                    bgcolor: alpha(color, 0.08),
                    color
                };
            }}
        >
            {moved && <TrendIcon sx={{fontSize: 16}}/>}

            <Typography component={"span"} variant={"body1"} fontWeight={700} lineHeight={1}>
                {moved ? comparison.change : "—"}
            </Typography>
        </Stack>
    );

    return panel == null ? chip : (
        <InsightsTooltip placement={"top"} title={panel}>
            {chip}
        </InsightsTooltip>
    );
}

export interface CardHeadlineChange {
    title: string;
    subtitle?: string;
    current: string;
    previous: string;
    delta: number | null | undefined;
    formatDelta: (delta: number) => string;
    currentLabel?: string;
    compareLabel?: string;
    lowerIsBetter?: boolean;
}

export interface CardHeadlineProps {
    value: string;
    suffix?: string;
    change?: CardHeadlineChange | null;
    comparison?: StatComparison;
    panel?: React.ReactNode;
}

/** Standard headline row for insight cards: figure and optional trend chip with comparison tooltip. */
export function CardHeadline({value, suffix, change, comparison, panel}: CardHeadlineProps) {
    const {t} = useTranslation();

    let comp = comparison;
    let pnl = panel;

    if (change != null) {
        comp = trendOf(change.delta, change.formatDelta, change.lowerIsBetter);
        pnl = (
            <React.Fragment>
                <TooltipTitle title={change.title} subtitle={change.subtitle}/>
                <TooltipRow label={change.currentLabel ?? t("Current")} value={change.current}/>
                <TooltipRow label={change.compareLabel ?? t("Compare period")} value={change.previous}/>
                <TooltipDelta label={t("Change")} value={change.delta} format={change.formatDelta}/>
            </React.Fragment>
        );
    }

    return (
        <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0, minHeight: "32px"}}>
            <HeadlineValue value={value} suffix={suffix}/>
            <TrendChip comparison={comp} panel={pnl}/>
        </Stack>
    );
}
